import { CurrentTOCItemState, ISelectedEntity } from '../contexts/formViewContext';
import { TaxReturnTOC } from '../model';

import { cloneDeep } from 'lodash';
import { TaxReturnItemDataType } from '../model/taxReturnItemDataType';
import { TaxReturnTOCItem } from '../model/taxReturnTOCItem';
import { TaxReturnTOCKey } from '../model/taxReturnTOCKey';

export const areTaxReturnTOCKeysEqual = (
    leftTocKey: TaxReturnTOCKey | null | undefined,
    rightTocKey: TaxReturnTOCKey | null | undefined
): boolean => {
    return areTocKeysEqual(leftTocKey, rightTocKey);
};
export const getTocItemKey = (item: TaxReturnTOCItem | null | undefined): TaxReturnTOCKey | undefined => {
    if (!item) {
        return undefined;
    }

    return item.taxReturnTOCKey ?? undefined;
};

export const areTocKeysEqual = (left: TaxReturnTOCKey | null | undefined, right: TaxReturnTOCKey | null | undefined): boolean => {
    if (!left || !right) {
        return false;
    }

    const hasSameTaxReturnItemType: boolean = left.taxReturnItemType === right.taxReturnItemType;
    const hasSameEntityCode: boolean = left.entityCode === right.entityCode;
    const hasSameFormPageId: boolean = left.formPageId === right.formPageId;
    const hasSameFormPageName: boolean = left.formPageName === right.formPageName;
    const hasSameContextKey: boolean = left.contextKey === right.contextKey;
    const hasSameStatementId: boolean = left.statementId === right.statementId;
    const hasSameBeginRow: boolean = left.beginRow === right.beginRow;
    const hasSameEndRow: boolean = left.endRow === right.endRow;
    const hasSameDataColumnId: boolean = left.dataColumnId === right.dataColumnId;
    const hasSameWorkpaperId: boolean = left.workpaperId === right.workpaperId;
    const hasSameTitle: boolean = left.title === right.title;

    return (
        hasSameTaxReturnItemType &&
        hasSameEntityCode &&
        hasSameFormPageId &&
        hasSameFormPageName &&
        hasSameContextKey &&
        hasSameStatementId &&
        hasSameBeginRow &&
        hasSameEndRow &&
        hasSameDataColumnId &&
        hasSameWorkpaperId &&
        hasSameTitle
    );
};

export const findTocItemByKey = (
    tocItems: TaxReturnTOCItem[] | null | undefined,
    tocKey: TaxReturnTOCKey | null | undefined
): TaxReturnTOCItem | undefined => {
    if (!tocItems || !tocKey) {
        return undefined;
    }

    for (const item of tocItems) {
        const hasMatchingKey: boolean = areTocKeysEqual(item.taxReturnTOCKey, tocKey);
        if (hasMatchingKey) {
            return item;
        }

        if (item.children && item.children.length > 0) {
            const childMatch: TaxReturnTOCItem | undefined = findTocItemByKey(item.children, tocKey);
            if (childMatch) {
                return childMatch;
            }
        }
    }

    return undefined;
};

export const getTocItemFromKeyMap = (
    tocReferenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem>,
    tocKey: TaxReturnTOCKey | null | undefined
): TaxReturnTOCItem | undefined => {
    if (!tocKey) {
        return undefined;
    }

    const directMatch: TaxReturnTOCItem | undefined = tocReferenceMap.get(tocKey);
    if (directMatch) {
        return directMatch;
    }

    for (const [mapKey, item] of tocReferenceMap.entries()) {
        const isMatch: boolean = areTocKeysEqual(mapKey, tocKey);
        if (isMatch) {
            return item;
        }
    }

    return undefined;
};

export const findTocItemById = (
    tocItems: TaxReturnTOCItem[] | null | undefined,
    tocItemId: string | null | undefined
): TaxReturnTOCItem | undefined => {
    if (!tocItems || !tocItemId) {
        return undefined;
    }

    for (const item of tocItems) {
        if (item.id === tocItemId) {
            return item;
        }

        if (item.children && item.children.length > 0) {
            const childMatch: TaxReturnTOCItem | undefined = findTocItemById(item.children, tocItemId);
            if (childMatch) {
                return childMatch;
            }
        }
    }

    return undefined;
};

export const findFirstTaxReturnTOCItem = (
    tocItems: TaxReturnTOCItem[],
    condition: (item: TaxReturnTOCItem) => boolean
): TaxReturnTOCItem | null => {
    for (const item of tocItems) {
        if (condition(item)) {
            return item;
        }

        if (item.children && item.children.length > 0) {
            const found = findFirstTaxReturnTOCItem(item.children, condition);

            if (found) {
                return item;
            }
        }
    }

    return null;
};

export const updateFirstTaxReturnTOCItem = (
    tocItems: TaxReturnTOCItem[],
    condition: (item: TaxReturnTOCItem) => boolean,
    updater: (item: TaxReturnTOCItem) => void
): void => {
    for (const item of tocItems) {
        if (condition(item)) {
            updater(item);
            return;
        }

        if (item.children && item.children.length > 0) {
            updateFirstTaxReturnTOCItem(item.children, condition, updater);
        }
    }
};

export const traverseTaxReturnTOCItem = (tocItem: TaxReturnTOCItem, callback: (item: TaxReturnTOCItem) => void) => {
    callback(tocItem);

    if (tocItem && tocItem.children) {
        for (const child of tocItem.children) {
            traverseTaxReturnTOCItem(child, callback);
        }
    }
};

export const updateTaxReturnTOCItemAndChildren = (
    tocItems: TaxReturnTOCItem[],
    tocItemId: string,
    updater: (item: TaxReturnTOCItem) => void
): boolean => {
    for (const item of tocItems) {
        if (item.id === tocItemId) {
            updateTOCItemsRecursively(item, updater);

            return true;
        }

        if (item.children && item.children.length > 0) {
            const found = updateTaxReturnTOCItemAndChildren(item.children, tocItemId, updater);

            if (found) {
                return true;
            }
        }
    }

    return false;
};

export const updateTOCItemsRecursively = (tocItem: TaxReturnTOCItem, updater: (item: TaxReturnTOCItem) => void) => {
    if (tocItem.children) {
        for (const child of tocItem.children) {
            updateTOCItemsRecursively(child, updater);
        }
    }

    updater(tocItem);
};

export const updateTOCItemsParents = (
    tocItem: TaxReturnTOCItem,
    tocItems: TaxReturnTOCItem[] | null | undefined,
    updater: (item: TaxReturnTOCItem) => void
): void => {
    if (!tocItems) {
        return;
    }

    const parentId: string | null | undefined = tocItem.parentId;
    if (!parentId) {
        return;
    }

    const parentElement: TaxReturnTOCItem | undefined = findTocItemById(tocItems, parentId);
    if (!parentElement) {
        return;
    }

    updater(parentElement);

    if (parentElement.parentId) {
        updateTOCItemsParents(parentElement, tocItems, updater);
    }
};

export const recursivelyFilterTOCItems = (tocItem: TaxReturnTOCItem, searchText: string): TaxReturnTOCItem | null => {
    const matchesSearchText = tocItem.title?.toLowerCase().includes(searchText.toLowerCase());

    if (matchesSearchText) {
        return tocItem;
    }

    if (tocItem.children) {
        const filteredChildren: TaxReturnTOCItem[] = [];

        for (const child of tocItem.children) {
            const filteredChild = recursivelyFilterTOCItems(child, searchText);

            if (filteredChild) {
                filteredChildren.push(filteredChild);
            }
        }

        if (filteredChildren.length > 0) {
            return { ...tocItem, children: filteredChildren };
        }
    }

    return null;
};

export const filterTOCItemsByVisibility = (items: TaxReturnTOCItem[] | null | undefined): TaxReturnTOCItem[] => {
    if (!items) {
        return [];
    }

    return items
        .filter((item: TaxReturnTOCItem) => (item.isVisibleInTOCView ?? false) === true)
        .map((item: TaxReturnTOCItem) => {
            const children: TaxReturnTOCItem[] | null | undefined = item.children
                ? filterTOCItemsByVisibility(item.children)
                : item.children;

            return {
                ...item,
                children,
            };
        });
};

export const getFilteredTOCItems = (taxReturnTOC: TaxReturnTOCItem[], searchText: string): TaxReturnTOCItem[] => {
    const result: TaxReturnTOCItem[] = [];

    for (const item of taxReturnTOC) {
        const filteredItem = recursivelyFilterTOCItems(item, searchText);

        if (filteredItem) {
            result.push(filteredItem);
        }
    }

    return result;
};

export const recursivelyFilterTOCItemsByCondition = (
    tocItem: TaxReturnTOCItem,
    condition: (item: TaxReturnTOCItem) => boolean
): TaxReturnTOCItem[] => {
    let result: TaxReturnTOCItem[] = [];

    if (tocItem.children && tocItem.children.length > 0) {
        for (const child of tocItem.children) {
            const filteredChild = recursivelyFilterTOCItemsByCondition(child, condition);

            if (filteredChild) {
                result = [...result, ...filteredChild];
            }
        }
    } else {
        if (condition(tocItem)) {
            result = [tocItem];
        }
    }

    return result;
};

export const getFilteredTOCItemsByCondition = (
    taxReturnTOC: TaxReturnTOCItem[],
    condition: (item: TaxReturnTOCItem) => boolean
): TaxReturnTOCItem[] => {
    let result: TaxReturnTOCItem[] = [];

    for (const item of taxReturnTOC) {
        const filteredItems = recursivelyFilterTOCItemsByCondition(item, condition);

        if (filteredItems) {
            result = [...result, ...filteredItems];
        }
    }

    return result;
};

const clearCurrentTaxReturnTOCItemOnTree = (toc: TaxReturnTOC) => {
    toc.items?.forEach((tocItem: TaxReturnTOCItem) => {
        traverseTaxReturnTOCItem(tocItem, (item: TaxReturnTOCItem) => {
            item.isSelected = false;
        });
    });
};

export const isIsolatedTocItem = (item: TaxReturnTOCItem, tocItems: TaxReturnTOCItem[] | null | undefined): boolean => {
    const hasParent = item.parentId !== null && item.parentId !== undefined;
    if (!hasParent) {
        return true;
    }

    const parentItem = findTocItemById(tocItems, item.parentId);
    return !parentItem?.taxReturnTOCKey;
};

export const resolveTocItemPageIndex = (item: TaxReturnTOCItem, tocItems: TaxReturnTOCItem[] | null | undefined): number => {
    const hasParent = item.parentId !== null && item.parentId !== undefined;
    if (!hasParent) {
        return 0;
    }

    const parentItem = findTocItemById(tocItems, item.parentId);
    if (!parentItem?.taxReturnTOCKey) {
        return 0;
    }

    return item.index ?? 0;
};

export const getUpdatedCurrentTOCItemStateOnTree = (
    taxReturnTOC: TaxReturnTOC | null,
    tocReferenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem>,
    tocItemKey: TaxReturnTOCKey | null | undefined,
    tocItemId: string | null | undefined,
    entity?: ISelectedEntity | undefined
): CurrentTOCItemState | undefined => {
    if (taxReturnTOC) {
        const toc: TaxReturnTOC = cloneDeep(taxReturnTOC);
        clearCurrentTaxReturnTOCItemOnTree(toc);

        if (tocItemKey || tocItemId) {
            const currentItemFromKey: TaxReturnTOCItem | undefined = tocItemKey
                ? getTocItemFromKeyMap(tocReferenceMap, tocItemKey)
                : undefined;
            const currentItemFromId: TaxReturnTOCItem | undefined = currentItemFromKey
                ? undefined
                : findTocItemById(toc.items, tocItemId ?? undefined);
            const currentItem: TaxReturnTOCItem | undefined = currentItemFromKey ?? currentItemFromId;

            if (!currentItem) {
                return undefined;
            }

            const resolvedTocKey: TaxReturnTOCKey | undefined = getTocItemKey(currentItem);

            const updatedCurrentTOCItemState: CurrentTOCItemState = {
                id: currentItem?.id || '',
                parentId: currentItem?.parentId || null,
                tocKey: resolvedTocKey ?? null,
                pageIndex: resolveTocItemPageIndex(currentItem, toc.items),
                tocItemTitle: currentItem?.title || '',
                entity: entity,
                isEnumerated: currentItem?.isEnumerated || false,
                dataName: currentItem?.dataName || '',
                canSelectEntity: currentItem?.canSelectEntity || true,
                defaultTocItem: taxReturnTOC.defaultItem,
                taxReturnItemType: currentItem?.taxReturnTOCKey?.taxReturnItemType,
                isConsolidated: currentItem?.isConsolidated || false,
            };

            return updatedCurrentTOCItemState;
        }
    }
};

export const getTocItemPagesAndParentId = (
    tocReferenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem>,
    tocItemKey: TaxReturnTOCKey | undefined,
    parentTocItemKey?: TaxReturnTOCKey | null | undefined
) => {
    const taxReturnTOCItem: TaxReturnTOCItem | undefined = tocItemKey ? getTocItemFromKeyMap(tocReferenceMap, tocItemKey) : undefined;

    if (!taxReturnTOCItem) {
        return {
            formPages: [],
            parentId: null,
        };
    }

    const hasParent: boolean = taxReturnTOCItem?.parentId !== null && taxReturnTOCItem?.parentId !== undefined;

    if (hasParent) {
        let parentItem: TaxReturnTOCItem | undefined = undefined;

        if (parentTocItemKey) {
            parentItem = getTocItemFromKeyMap(tocReferenceMap, parentTocItemKey);
        }

        if (!parentItem) {
            const parentId: string | null | undefined = taxReturnTOCItem.parentId;
            if (parentId) {
                for (const item of tocReferenceMap.values()) {
                    if (item.id === parentId) {
                        parentItem = item;
                        break;
                    }
                }
            }
        }

        if (parentItem?.taxReturnTOCKey) {
            return {
                formPages: parentItem.children,
                parentId: taxReturnTOCItem.parentId || '',
            };
        }
    }

    return {
        formPages: [taxReturnTOCItem],
        parentId: null,
    };
};

export const expandParentNodes = (tocItems: TaxReturnTOCItem[] | null | undefined, tocItemId: string): void => {
    const targetItem: TaxReturnTOCItem | undefined = findTocItemById(tocItems, tocItemId);

    if (!targetItem || !targetItem.parentId) {
        return;
    }

    let currentParentId: string | null | undefined = targetItem.parentId;
    while (currentParentId) {
        const parentItem: TaxReturnTOCItem | undefined = findTocItemById(tocItems, currentParentId);
        if (parentItem) {
            parentItem.isExpanded = true;
            currentParentId = parentItem.parentId;
        } else {
            break;
        }
    }
};

export const buildTocMaps = (taxReturnTOCItems: TaxReturnTOCItem[]) => {
    const tocReferenceMap = new Map<TaxReturnTOCKey, TaxReturnTOCItem>();
    const dataNameToTocItemMap = new Map<string, TaxReturnTOCItem>();

    for (const item of taxReturnTOCItems) {
        traverseTaxReturnTOCItem(item, (item) => {
            const tocKey: TaxReturnTOCKey | undefined = getTocItemKey(item);
            if (tocKey) {
                tocReferenceMap.set(tocKey, item);
            }

            if (item.dataName && item.isStatement && item.isDetailedWorkspace) {
                dataNameToTocItemMap.set(item.dataName!, item);
            }
        });
    }

    return { tocReferenceMap, dataNameToTocItemMap };
};

export const buildEmptyDetailedWorkspaceItemsMap = (itemsToAdd: TaxReturnTOCItem[] | null | undefined) => {
    const emptyDetailedWorkspaceItemsMap = new Map<string, TaxReturnTOCItem>();

    if (itemsToAdd) {
        for (const item of itemsToAdd) {
            if (item.dataName) {
                emptyDetailedWorkspaceItemsMap.set(item.dataName, item);
            }
        }
    }

    return emptyDetailedWorkspaceItemsMap;
};

export const collectTocItemIds = (items: TaxReturnTOCItem[] | null | undefined): string[] => {
    const ids: string[] = [];

    if (!items) {
        return ids;
    }

    for (const item of items) {
        traverseTaxReturnTOCItem(item, (tocItem: TaxReturnTOCItem) => {
            if (tocItem.id) {
                ids.push(tocItem.id);
            }
        });
    }

    return ids;
};

export const hasDuplicateTocItemId = (items: TaxReturnTOCItem[] | null | undefined, tocItemId: string | null | undefined): boolean => {
    if (!items || !tocItemId) {
        return false;
    }

    let matchCount = 0;

    for (const item of items) {
        traverseTaxReturnTOCItem(item, (tocItem: TaxReturnTOCItem) => {
            if (tocItem.id === tocItemId) {
                matchCount += 1;
            }
        });
    }

    return matchCount > 1;
};

/**
 * Ids that appear more than once across the tree. Computed once (e.g. via useMemo keyed on the
 * TOC data) so callers can do an O(1) Set.has() check instead of re-traversing the tree per lookup.
 */
export const getDuplicateTocItemIds = (items: TaxReturnTOCItem[] | null | undefined): Set<string> => {
    const idCounts = new Map<string, number>();

    if (items) {
        for (const item of items) {
            traverseTaxReturnTOCItem(item, (tocItem: TaxReturnTOCItem) => {
                if (tocItem.id) {
                    idCounts.set(tocItem.id, (idCounts.get(tocItem.id) ?? 0) + 1);
                }
            });
        }
    }

    const duplicateIds = new Set<string>();
    for (const [id, count] of idCounts) {
        if (count > 1) {
            duplicateIds.add(id);
        }
    }

    return duplicateIds;
};

/**
 * Form page name used to disambiguate duplicate TOC ids in URLs and selection.
 * Prefers taxReturnTOCKey.formPageName when present, then falls back to the TOC title.
 */
export const getTocItemFormPageName = (item: TaxReturnTOCItem | null | undefined): string => {
    if (!item) {
        return '';
    }

    const formPageName: string | null | undefined = item.taxReturnTOCKey?.formPageName;
    if (formPageName) {
        return formPageName;
    }

    return item.title ?? '';
};

/**
 * Groups every item (including nested children) by id. Computed once per tree (e.g. via useMemo
 * keyed on the rendered items) so a click handler can resolve candidates without re-traversing.
 */
export const groupTocItemsById = (items: TaxReturnTOCItem[] | null | undefined): Map<string, TaxReturnTOCItem[]> => {
    const itemsById = new Map<string, TaxReturnTOCItem[]>();

    if (items) {
        for (const item of items) {
            traverseTaxReturnTOCItem(item, (tocItem: TaxReturnTOCItem) => {
                if (tocItem.id) {
                    const existing: TaxReturnTOCItem[] | undefined = itemsById.get(tocItem.id);
                    if (existing) {
                        existing.push(tocItem);
                    } else {
                        itemsById.set(tocItem.id, [tocItem]);
                    }
                }
            });
        }
    }

    return itemsById;
};

/**
 * Picks the right item among same-id candidates. A single candidate is returned outright (the
 * common, non-duplicate case). With multiple candidates, matches by formPageName when available,
 * falling back to the first candidate rather than resolving to nothing.
 */
export const resolveTocItemFromCandidates = (
    candidates: TaxReturnTOCItem[] | undefined,
    formPageNameFromDom?: string | null
): TaxReturnTOCItem | undefined => {
    if (!candidates || candidates.length <= 1) {
        return candidates?.[0];
    }

    const normalizedFormPageName: string = formPageNameFromDom ?? '';
    return candidates.find((candidate) => getTocItemFormPageName(candidate) === normalizedFormPageName) ?? candidates[0];
};

export const findTocItemByIdAndFormPageName = (
    tocItems: TaxReturnTOCItem[] | null | undefined,
    tocItemId: string | null | undefined,
    formPageName?: string | null
): TaxReturnTOCItem | undefined => {
    if (!tocItems || !tocItemId) {
        return undefined;
    }

    const normalizedFormPageName: string = formPageName ?? '';

    for (const item of tocItems) {
        if (item.id === tocItemId && getTocItemFormPageName(item) === normalizedFormPageName) {
            return item;
        }

        if (item.children && item.children.length > 0) {
            const childMatch: TaxReturnTOCItem | undefined = findTocItemByIdAndFormPageName(item.children, tocItemId, formPageName);
            if (childMatch) {
                return childMatch;
            }
        }
    }

    return undefined;
};

export const collectExpandedTocItemIds = (items: TaxReturnTOCItem[] | null | undefined): string[] => {
    const ids: string[] = [];

    if (!items) {
        return ids;
    }

    for (const item of items) {
        traverseTaxReturnTOCItem(item, (tocItem: TaxReturnTOCItem) => {
            if (tocItem.isExpanded && tocItem.id) {
                ids.push(tocItem.id);
            }
        });
    }

    return ids;
};

export const collectParentTocItemIds = (tocItems: TaxReturnTOCItem[] | null | undefined, tocItemId: string): string[] => {
    const ids: string[] = [];
    const targetItem: TaxReturnTOCItem | undefined = findTocItemById(tocItems, tocItemId);
    let currentParentId: string | null | undefined = targetItem?.parentId;

    while (currentParentId) {
        ids.push(currentParentId);
        const parentItem: TaxReturnTOCItem | undefined = findTocItemById(tocItems, currentParentId);
        currentParentId = parentItem?.parentId;
    }

    return ids;
};

/**
 * Checks if the TOC items represent an overflow statement.
 * Overflow occurs ONLY when:
 * 1. All items are statements (isStatement == true)
 * 2. ANY item is WhitePaper (taxReturnItemDataType === WhitePaper)
 */
export const isOverflowStatement = (taxReturnTOCItems: TaxReturnTOCItem[]): boolean => {
    if (!taxReturnTOCItems || taxReturnTOCItems.length === 0) {
        return false;
    }

    // First check: All items must be statements
    const allAreStatements = taxReturnTOCItems.every((item) => item.isStatement === true);
    if (!allAreStatements) {
        return false;
    }

    const hasWhitePaper = taxReturnTOCItems.some((item) => item.taxReturnItemDataType === TaxReturnItemDataType.WhitePaper);

    return hasWhitePaper;
};
